// App-side community-gallery orchestration. The headless query and its wire
// encoding live behind `@heidi/templates/data`; this hook owns app filters,
// specialty expansion, debounce, and app analytics.
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- preserve the legacy helper's dual v1/v2 analytics emission while this hook moves to a refresh-safe .ts delegate
import type { TemplateCategory } from '@heidi/schema/template-gallery';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo } from 'react';
import {
  FilterSettings,
  GET_COMMUNITY_TEMPLATES,
  TemplateCommunitySortBy,
} from '../../../contracts';
import {
  trackCommunityTemplateSearched,
  useQueryCommunityTemplates as useQueryCommunityTemplatesCore,
} from '../../../data';
import { getTemplateDetailsPorts } from '../../template-details/template-details-ports';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- preserve the legacy analytics helper without changing its PostHog behavior

export interface CommunityTemplatesParams {
  page?: number;
  sort?: TemplateCommunitySortBy;
  location?: string;
  specialty?: string;
  category?: TemplateCategory | FilterSettings.ALL;
  search?: string;
  enabled?: boolean;
  specialties?: CommunitySpecialty[];
}

interface CommunitySpecialty {
  name: string;
  groups?: { name: string }[];
}

export function useQueryCommunityTemplates(params: CommunityTemplatesParams = {}) {
  const {
    page = 1,
    sort = TemplateCommunitySortBy.MOST_POPULAR,
    location: locationParam = FilterSettings.ALL,
    specialty: specialtyParam = FilterSettings.ALL,
    category: categoryParam = FilterSettings.ALL,
    search: searchParam = '',
    enabled = true,
    specialties: specialtiesData = [],
  } = params;

  const location =
    locationParam === String(FilterSettings.ALL) ||
    locationParam === String(FilterSettings.UNDEFINED) ||
    locationParam === String(FilterSettings.INITIAL)
      ? undefined
      : locationParam;

  const specialty =
    specialtyParam === String(FilterSettings.ALL) ||
    specialtyParam === String(FilterSettings.UNDEFINED) ||
    specialtyParam === String(FilterSettings.INITIAL)
      ? undefined
      : specialtyParam;

  const category = categoryParam === FilterSettings.ALL ? undefined : categoryParam;
  const search = searchParam === '' ? undefined : searchParam;

  const specialtyGroups = useMemo(() => {
    if (!specialty || !specialtiesData) return undefined;
    const matchedSpecialty = specialtiesData.find((s) => s.name === specialty);
    return matchedSpecialty?.groups?.map((g) => g.name);
  }, [specialty, specialtiesData]);
  const debouncedSearch = useDebounce(search, 500);

  // Track search analytics when debounced search changes.
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      // eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- preserve the legacy PostHog event alongside the v2 event
      trackCommunityTemplateSearched({
        search: debouncedSearch,
      });
    }
    // eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- preserve the legacy helper dependency while this hook moves to a refresh-safe .ts delegate
  }, [debouncedSearch]);

  return useQueryCommunityTemplatesCore({
    sort,
    location,
    specialty,
    category,
    specialtyGroups,
    search: debouncedSearch,
    page,
    enabled,
  });
}

type GallerySpecialties = {
  id: string;
  name: string;
  localizedName: string;
  groups?: { name: string }[];
}[];

/** The specialties list a gallery query selects through the templates hook. */
export const useQueryTemplateSpecialties = (
  select: (data: { specialties: GallerySpecialties }) => GallerySpecialties
) => getTemplateDetailsPorts().useQuerySpecialties((data) => select(data));

export { GET_COMMUNITY_TEMPLATES };
